import { AppLayout } from "@/components/layout/AppLayout";
import { useFleetOverview, VehicleOverview, StabilityLevel } from "@/hooks/useFleetOverview";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Activity,
  AlertTriangle,
  Bell,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  Gauge,
  ShieldCheck,
  Wrench,
  Car,
  BarChart3,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { useState } from "react";

/* ─── Summary card helpers ─── */

const stabilityConfig: Record<StabilityLevel, { color: string; bg: string }> = {
  High: { color: "text-emerald-600", bg: "bg-emerald-500/10" },
  Medium: { color: "text-amber-600", bg: "bg-amber-500/10" },
  Low: { color: "text-red-600", bg: "bg-red-500/10" },
};

const impactBadge: Record<string, string> = {
  high: "bg-red-500/10 text-red-600 border-red-500/20",
  medium: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  low: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
};

/* ─── Metric Pill ─── */
const SummaryPill = ({ icon: Icon, label, value, sub }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  sub?: string;
}) => (
  <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-muted/50">
    <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
    <div>
      <p className="text-lg font-bold text-foreground leading-none">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  </div>
);

/* ─── Vehicle Row ─── */
const PAGE_SIZE = 7;

const VehicleRow = ({ v }: { v: VehicleOverview }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className={v.needsAttention ? "" : "opacity-75"}>
      <CardContent className="p-4">
        <button
          className="w-full text-left flex items-center gap-4"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-center h-9 w-9 rounded-full bg-muted shrink-0">
            <Car className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-foreground">{v.vehicleName}</span>
              <Badge variant="outline" className={impactBadge[v.impact]}>
                {v.impact.charAt(0).toUpperCase() + v.impact.slice(1)} impact
              </Badge>
              {v.tasks.overdue > 0 && (
                <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20 text-xs">
                  {v.tasks.overdue} overdue
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{v.primaryReason}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right">
              <p className="text-sm font-bold text-foreground">{v.attentionScore}</p>
              <p className="text-xs text-muted-foreground">Score</p>
            </div>
            {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </div>
        </button>

        {expanded && (
          <div className="mt-4 pt-4 border-t space-y-4">
            {/* Key metrics */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Key Metrics</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MetricCell
                  icon={DollarSign}
                  label="Monthly Profit"
                  value={`$${Math.round(v.metrics.monthlyProfit)}`}
                  negative={v.metrics.monthlyProfit < 0}
                />
                <MetricCell icon={Gauge} label="Utilization" value={`${Math.round(v.metrics.utilization)}%`} />
                <MetricCell icon={BarChart3} label="Expense Ratio" value={`${Math.round(v.metrics.expenseRatio)}%`} />
                <MetricCell
                  icon={v.metrics.recentExpenseChange > 0 ? TrendingUp : TrendingDown}
                  label="Expense Trend"
                  value={`${v.metrics.recentExpenseChange > 0 ? "+" : ""}${Math.round(v.metrics.recentExpenseChange)}%`}
                  negative={v.metrics.recentExpenseChange > 25}
                />
              </div>
            </div>

            {/* Alerts & Tasks */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Alerts</p>
                {v.alertCount === 0 ? (
                  <p className="text-sm text-muted-foreground">No active alerts</p>
                ) : (
                  <div className="flex items-center gap-2 text-sm">
                    <Bell className="h-3.5 w-3.5 text-amber-500" />
                    <span className="text-foreground font-medium">{v.alertCount} active alert{v.alertCount !== 1 ? "s" : ""}</span>
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Tasks</p>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <Wrench className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-muted-foreground">{v.tasks.open} open</span>
                    {v.tasks.overdue > 0 && <span className="text-red-600 font-medium">({v.tasks.overdue} overdue)</span>}
                  </div>
                  {v.tasks.upcoming > 0 && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">{v.tasks.upcoming} upcoming (next 14 days)</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MetricCell = ({ icon: Icon, label, value, negative }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  negative?: boolean;
}) => (
  <div className="flex items-center gap-2">
    <Icon className={`h-3.5 w-3.5 ${negative ? "text-red-500" : "text-muted-foreground"}`} />
    <div>
      <p className={`text-sm font-medium ${negative ? "text-red-600" : "text-foreground"}`}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  </div>
);

/* ─── Task & State Summary ─── */
const TaskStateSummary = ({ vehicles }: { vehicles: VehicleOverview[] }) => {
  const withIssues = vehicles.filter((v) => v.tasks.open > 0 || v.tasks.overdue > 0);
  if (withIssues.length === 0) return null;

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Task & State Summary</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Open tasks and upcoming maintenance at a glance.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {withIssues.slice(0, 6).map((v) => (
          <Card key={v.vehicleId}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Car className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-foreground text-sm">{v.vehicleName}</span>
              </div>
              <div className="space-y-1 text-sm">
                {v.tasks.overdue > 0 && (
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                    <span className="text-red-600 font-medium">{v.tasks.overdue} overdue task{v.tasks.overdue !== 1 ? "s" : ""}</span>
                  </div>
                )}
                {v.tasks.open > 0 && (
                  <div className="flex items-center gap-2">
                    <Wrench className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-muted-foreground">{v.tasks.open} open task{v.tasks.open !== 1 ? "s" : ""}</span>
                  </div>
                )}
                {v.tasks.upcoming > 0 && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-muted-foreground">{v.tasks.upcoming} upcoming</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

/* ─── Main Page ─── */
const FleetOverview = () => {
  const { attentionVehicles, stableVehicles, summary, vehicles, isLoading } = useFleetOverview();
  const [showAll, setShowAll] = useState(false);

  const visibleAttention = showAll ? attentionVehicles : attentionVehicles.slice(0, PAGE_SIZE);
  const hasMore = attentionVehicles.length > PAGE_SIZE;

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-8 max-w-3xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">Fleet Overview</h1>
          <p className="text-muted-foreground mt-1">
            Ranked focus view of vehicles, tasks, and operational state.
          </p>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
            </div>
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
        ) : vehicles.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Activity className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-semibold text-foreground">Need fleet data</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Add vehicles and import trips and expenses to see your fleet overview.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <SummaryPill
                icon={DollarSign}
                label="Fleet Profit (Mo)"
                value={`$${Math.round(summary.monthlyProfit).toLocaleString()}`}
              />
              <SummaryPill
                icon={AlertTriangle}
                label="Need Attention"
                value={summary.vehiclesNeedingAttention}
                sub={`of ${vehicles.length} vehicles`}
              />
              <SummaryPill
                icon={Bell}
                label="Active Alerts"
                value={summary.activeAlerts}
              />
              <div className={`flex items-center gap-3 px-4 py-3 rounded-lg ${stabilityConfig[summary.stability].bg}`}>
                <ShieldCheck className={`h-5 w-5 ${stabilityConfig[summary.stability].color} shrink-0`} />
                <div>
                  <p className={`text-lg font-bold leading-none ${stabilityConfig[summary.stability].color}`}>
                    {summary.stability}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">Stability</p>
                </div>
              </div>
            </div>

            {/* Section 1: Needs Attention */}
            {attentionVehicles.length > 0 && (
              <div className="space-y-3">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Needs Attention</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Ranked by financial impact, risk, and operational state.
                  </p>
                </div>
                <div className="space-y-2">
                  {visibleAttention.map((v) => (
                    <VehicleRow key={v.vehicleId} v={v} />
                  ))}
                </div>
                {hasMore && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-muted-foreground"
                    onClick={() => setShowAll(!showAll)}
                  >
                    {showAll ? "Show fewer" : `Show all ${attentionVehicles.length} vehicles`}
                  </Button>
                )}
              </div>
            )}

            {/* Section 2: Stable */}
            {stableVehicles.length > 0 && (
              <div className="space-y-3">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Stable — No Immediate Action Needed</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    These vehicles are performing within normal ranges. Safe to deprioritize.
                  </p>
                </div>
                <div className="space-y-2">
                  {stableVehicles.map((v) => (
                    <VehicleRow key={v.vehicleId} v={v} />
                  ))}
                </div>
              </div>
            )}

            {/* Section 3: Task & State Summary */}
            <TaskStateSummary vehicles={vehicles} />

            {/* All-clear state */}
            {attentionVehicles.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground">Fleet looks good</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    No vehicles need immediate attention right now.
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default FleetOverview;
